/**
 * Map an `updateAutoTrainStatus` WebSocket event — the terminal outcome of a
 * scheduled auto-train, published by backend_consumer's settle thread
 * (optuneutils.auto_train) — to a local usecase patch + toast.
 *
 * The backend has already cleared `usecase.auto_train_pending` before
 * publishing, so every terminal outcome clears the local mirror; the caller
 * also refetches usecases so the server document reconciles everything else.
 */

export const AUTO_TRAIN_WS_STARTED_TRAINING = 'started_training';
export const AUTO_TRAIN_WS_NO_DATA = 'no_data';
export const AUTO_TRAIN_WS_TRAIN_FAILED = 'train_failed';
export const AUTO_TRAIN_WS_ALL_FAILED = 'all_failed';

const TERMINAL_STATUSES = new Set([
    AUTO_TRAIN_WS_STARTED_TRAINING,
    AUTO_TRAIN_WS_NO_DATA,
    AUTO_TRAIN_WS_TRAIN_FAILED,
    AUTO_TRAIN_WS_ALL_FAILED,
]);

const DEFAULT_MESSAGES = {
    [AUTO_TRAIN_WS_STARTED_TRAINING]: 'Training started automatically after the data flows finished',
    [AUTO_TRAIN_WS_NO_DATA]: "The data flows finished but their data never arrived - training was not started",
    [AUTO_TRAIN_WS_TRAIN_FAILED]: 'Training could not be started after the data flows finished',
    [AUTO_TRAIN_WS_ALL_FAILED]: 'Automatic training canceled - no data flow completed successfully',
};

/**
 * @param {{usecaseId?: string, status?: string, message?: string}} event
 * @param {{hasLocalPending?: boolean}} [options] - when the local usecase copy
 *   no longer carries `auto_train_pending` (another handler already reconciled
 *   and toasted), the patch still applies but the toast is suppressed so the
 *   user is not notified twice.
 * @returns {{usecasePatch: Object, toast: {type: 'success'|'info'|'error', text: string}|null}|null}
 */
export function resolveAutoTrainStatusUpdate(event, { hasLocalPending = true } = {}) {
    const status = event?.status;
    if (!event?.usecaseId || !TERMINAL_STATUSES.has(status)) return null;

    const text = event.message || DEFAULT_MESSAGES[status];
    let type = 'error';
    if (status === AUTO_TRAIN_WS_STARTED_TRAINING) type = 'success';
    else if (status === AUTO_TRAIN_WS_ALL_FAILED) type = 'info';

    return {
        usecasePatch: { auto_train_pending: null },
        toast: hasLocalPending ? { type, text } : null,
    };
}
